import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import ReactApexChart from 'react-apexcharts';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const BiometricEfficiencyDonutChart = ({ filters }) => {
  const { accessToken } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });

        const response = await api.get(
          `${baseUrl}/superAdminDashboard/getBiometricefficiencyVsLeakage`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filteredParams,
          }
        );

        if (
          response.data?.success &&
          response.data.avgEfficiencyPercentage != null &&
          response.data.leakagePercentage != null
        ) {
          const efficiency = parseFloat(response.data.avgEfficiencyPercentage);
          const leakage = parseFloat(response.data.leakagePercentage);
          const valid = !isNaN(efficiency) && !isNaN(leakage) && (efficiency > 0 || leakage > 0);
          setChartData(valid ? [efficiency, leakage] : null);
        } else {
          setChartData(null);
        }
      } catch (err) {
        console.error('Failed to load Biometric efficiency chart:', err);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchData();
  }, [filters, accessToken]);

  const chartOptions = {
    chart: { type: 'donut', height: 300, toolbar: { show: false } },
    labels: ['Biometric Efficiency', 'Leakage'],
    colors: ['#1A2B5C', '#EE1D23'],
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
      style: { fontSize: '15px' },
    },
    tooltip: {
      y: { formatter: (val) => `${val}%` },
      custom: ({ seriesIndex, w }) => {
        const label = w.globals.labels[seriesIndex];
        const value = w.globals.series[seriesIndex];
        return `<div style="background:#fff;padding:6px 10px;border-radius:5px;color:#000;font-size:13px;">
          <strong>${label}:</strong> ${value}%
        </div>`;
      },
    },
    legend: {
      position: 'bottom',
      fontSize: '13px',
      fontWeight: 500,
    },
  };

  const SpinnerRing = () => (
    <div className="relative h-12 w-12 mb-4">
      <div
        className="absolute inset-0 rounded-full border-4 animate-spin"
        style={{
          borderTopColor: '#EE1D23',
          borderRightColor: '#1A2B5C',
          borderBottomColor: 'transparent',
          borderLeftColor: 'transparent',
          borderStyle: 'solid',
        }}
      />
      <div className="absolute inset-2 rounded-full bg-white" />
    </div>
  );

  const LoadingScreen = () => (
    <div className="flex flex-col items-center justify-center h-[250px] text-center animate-glowIn">
      <SpinnerRing />
      <div className="text-sm text-gray-500 font-medium tracking-wide">
        Preparing your insights...
      </div>
    </div>
  );

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-[250px] text-center animate-glowIn">
      <SpinnerRing />
      <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
      <p className="text-sm text-gray-500 max-w-sm px-4">
        We couldn’t retrieve any efficiency data. Please check your filters or try again later.
      </p>
    </div>
  );

  return (
    <div className="rounded-2xl bg-white shadow-xl p-6">
      <style>
        {`
        @keyframes glowIn {
          0% { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-glowIn {
          animation: glowIn 0.6s ease-out both;
        }
        `}
      </style>

      <h2 className="text-lg font-semibold text-center text-[#1A2B5C] mb-4">
        Biometric Efficiency vs Leakage
      </h2>

      {loading ? (
        <LoadingScreen />
      ) : chartData ? (
        <ReactApexChart
          options={chartOptions}
          series={chartData}
          type="donut"
          height={300}
        />
      ) : (
        <NoDataMessage />
      )}
    </div>
  );
};

export default BiometricEfficiencyDonutChart;
